import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  HardHat,
  Factory,
  Shirt,
  Flame,
  Zap,
  Droplets,
  Cpu,
  UtensilsCrossed,
  ExternalLink,
  MapPin,
  Calendar,
} from 'lucide-react';

import AnimatedCounter from '../components/AnimatedCounter';
import WorldMap from '../components/WorldMap';
import HeroVideo from '../components/HeroVideo';
import { SectionHeader, FadeUp } from '../components/SectionHeader';
import {
  METRICS,
  COMPANIES,
  INDUSTRIES,
  PROJECTS,
  IMAGES,
  BRAND,
} from '../data/content';
import { HERO_VIDEO } from '../data/about';
import { HERO, HOMEPAGE, GROUP } from '../constants/testIds';

const ICONS = {
  Building2,
  HardHat,
  Factory,
  Shirt,
  Flame,
  Zap,
  Droplets,
  Cpu,
  UtensilsCrossed,
};

export default function Home() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroVideo
            poster={HERO_VIDEO.poster}
            fallback={HERO_VIDEO.fallback}
            sources={HERO_VIDEO.sources}
          />
          <div className="absolute inset-0 hero-gradient" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="overline mb-6 flex items-center gap-3"
          >
            <span className="gold-rule" />
            Est. {BRAND.established} · {BRAND.hq}
          </motion.div>
          <motion.h1
            data-testid={HERO.headline}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-white font-bold tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] max-w-5xl"
          >
            Engineering Progress
            <br />
            <span className="text-[#D4AF37]">Across Industries.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-8 text-white/80 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            Delivering infrastructure, industrial engineering, advanced automation, digital
            technologies and consumer experiences through a unified platform built on
            execution excellence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/group-companies"
              data-testid={HERO.primaryCta}
              className="btn-primary"
            >
              Explore Our Businesses <ArrowRight size={17} />
            </Link>
            <Link to="/contact" data-testid={HERO.secondaryCta} className="btn-secondary">
              Contact Us <ArrowUpRight size={17} />
            </Link>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-[10px] uppercase tracking-[0.3em] flex items-center gap-2">
          <span className="w-6 h-px bg-white/50" /> Scroll <span className="w-6 h-px bg-white/50" />
        </div>
      </section>

      {/* METRICS */}
      <section
        data-testid={HOMEPAGE.metricsSection}
        className="bg-[#0D1B2A] relative overflow-hidden grain"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-24 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-12 gap-x-8">
          {METRICS.map((m) => (
            <AnimatedCounter key={m.label} {...m} />
          ))}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
      </section>

      {/* WHO WE ARE */}
      <section data-testid={HOMEPAGE.whoWeAre} className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="lg:col-span-6">
            <SectionHeader
              overline="Who We Are"
              title="Building Capability. Delivering Impact."
              lead="Clan Group is a diversified industrial group bringing together engineering expertise, project execution capability and innovative technologies to deliver solutions across infrastructure, industrial EPC, automation, technology and consumer sectors."
            />
            <FadeUp delay={0.15} className="mt-7 text-[#4B5563] text-[15px] leading-relaxed max-w-xl">
              With operations spanning India, Africa and the Middle East, the group continues
              to expand its capabilities while maintaining a strong focus on quality, safety
              and long-term value creation.
            </FadeUp>
            <FadeUp delay={0.25} className="mt-10 flex flex-wrap gap-3">
              <Link to="/about" className="btn-ghost-dark">
                About Clan Group <ArrowRight size={16} />
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 text-[#0D1B2A] font-semibold border-b border-[#0D1B2A] pb-1 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
              >
                See our projects <ArrowUpRight size={15} />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-6 relative">
            <FadeUp>
              <div className="relative">
                <img
                  src={IMAGES.team}
                  alt="Clan Group team"
                  className="w-full h-[460px] md:h-[560px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 md:-left-10 bg-[#D4AF37] text-[#0D1B2A] p-6 md:p-8 max-w-[260px]">
                  <div className="font-stat text-4xl font-extrabold">15+</div>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold mt-1">
                    Years of execution
                  </div>
                </div>
                <div className="absolute -top-5 -right-5 md:-right-10 bg-white border border-[#0D1B2A]/10 px-5 py-4 shadow-xl">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                    Headquartered
                  </div>
                  <div className="font-display font-bold text-[#0D1B2A]">Mumbai · India</div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* GROUP COMPANIES */}
      <section
        data-testid={HOMEPAGE.groupCompanies}
        className="bg-[#F5F7FA] py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionHeader
              overline="Group Companies"
              title="Six businesses. One platform."
              lead="An integrated portfolio of specialist businesses, each leading its discipline — operating with a shared standard of engineering and execution."
            />
            <Link
              to="/group-companies"
              className="inline-flex items-center gap-2 text-[#0D1B2A] font-semibold whitespace-nowrap hover:text-[#D4AF37] transition-colors"
            >
              View all companies <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {COMPANIES.map((c, i) => (
              <motion.article
                key={c.slug}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={GROUP.card(c.slug)}
                className="group bg-white overflow-hidden card-hover-lift flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div
                    className="absolute top-0 left-0 h-1 w-20"
                    style={{ background: c.accent }}
                  />
                  {c.external && (
                    <div className="absolute top-4 right-4 bg-white/95 text-[#0D1B2A] text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 inline-flex items-center gap-1">
                      External <ExternalLink size={10} />
                    </div>
                  )}
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#D4AF37]">
                    {c.discipline}
                  </div>
                  <h3 className="font-display font-bold text-[#0D1B2A] text-2xl mt-2">
                    {c.name}
                  </h3>
                  {c.tag && (
                    <div className="text-xs text-[#2A9D8F] font-semibold mt-1 tracking-wide">
                      {c.tag}
                    </div>
                  )}
                  <p className="mt-4 text-[14px] text-[#4B5563] leading-relaxed">
                    {c.summary}
                  </p>
                  <ul className="mt-5 flex flex-wrap gap-1.5">
                    {c.capabilities.slice(0, 4).map((cap) => (
                      <li
                        key={cap}
                        className="text-[11px] text-[#0D1B2A] bg-[#F5F7FA] px-2.5 py-1 border border-[#0D1B2A]/5"
                      >
                        {cap}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 pt-5 border-t border-[#0D1B2A]/8">
                    {c.external ? (
                      <a
                        href={c.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={GROUP.cardCta(c.slug)}
                        className="inline-flex items-center gap-2 text-[#0D1B2A] font-bold tracking-wide text-sm hover:text-[#D4AF37] transition-colors"
                      >
                        {c.cta || 'Learn More'} <ExternalLink size={14} />
                      </a>
                    ) : (
                      <Link
                        to={c.href}
                        data-testid={GROUP.cardCta(c.slug)}
                        className="inline-flex items-center gap-2 text-[#0D1B2A] font-bold tracking-wide text-sm hover:text-[#D4AF37] transition-colors"
                      >
                        Learn More <ArrowRight size={15} />
                      </Link>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid={HOMEPAGE.industries} className="bg-white py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader
            overline="Industries Served"
            title="Engineering for the industries that build economies."
          />
          <div className="mt-14 grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-9 gap-3 md:gap-4">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ICONS[ind.icon];
              return (
                <motion.div
                  key={ind.name}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="group aspect-square border border-[#0D1B2A]/10 flex flex-col items-center justify-center gap-3 text-center p-3 hover:border-[#D4AF37] hover:bg-[#0D1B2A] transition-colors duration-300"
                >
                  <Icon
                    size={28}
                    className="text-[#0D1B2A] group-hover:text-[#D4AF37] transition-colors"
                  />
                  <div className="text-[11px] md:text-[12px] font-display font-semibold text-[#0D1B2A] group-hover:text-white transition-colors leading-tight">
                    {ind.name}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section data-testid={HOMEPAGE.projects} className="bg-[#0D1B2A] py-24 md:py-32 relative grain">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionHeader
              overline="Featured Projects"
              title="Execution at scale, across geographies."
              lead="Selected projects from India, Africa and the Middle East — delivered with precision, safety and engineering rigor."
              light
            />
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 text-white font-semibold whitespace-nowrap hover:text-[#D4AF37] transition-colors"
            >
              All projects <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group overflow-hidden bg-[#132338] border border-white/8"
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#D4AF37]">
                    <span>{p.industry}</span>
                    <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />
                    <span className="text-white/60">{p.year}</span>
                  </div>
                  <h3 className="font-display text-white font-bold text-xl mt-3 leading-snug">
                    {p.name}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/65">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={12} /> {p.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} /> {p.status}
                    </span>
                  </div>
                  <p className="mt-5 text-sm text-white/70 leading-relaxed">{p.scope}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL MAP */}
      <section data-testid={HOMEPAGE.globalMap} className="bg-[#F5F7FA] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <SectionHeader
              overline="Global Presence"
              title="A platform with international reach."
              lead="From our base in Mumbai, we deliver projects across India, the Middle East and Africa — with expansion zones across emerging industrial corridors."
            />
            <FadeUp delay={0.2} className="mt-8">
              <Link to="/global-presence" className="btn-ghost-dark">
                Explore the map <ArrowRight size={16} />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <WorldMap compact />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid={HOMEPAGE.cta} className="bg-[#0D1B2A] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(212,175,55,0.15) 0%, transparent 60%)`,
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-5 flex items-center gap-3">
              <span className="gold-rule" /> Partner With Clan Group
            </div>
            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Have a project that needs <span className="text-[#D4AF37]">precision, scale</span> and{' '}
              <span className="text-[#D4AF37]">accountability?</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-2xl">
              Talk to our team about turnkey delivery, industrial engineering, automation systems
              and technology partnerships.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/contact" className="btn-primary justify-center">
              Start a Conversation <ArrowRight size={16} />
            </Link>
            <Link to="/group-companies" className="btn-secondary justify-center">
              Explore Capabilities <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
